(function () {
  const selector = 'media-gallery[data-product-slider]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setHeadlineContrast(gallery) {
    const headline = gallery.querySelector('.bs-product-slider__headline');
    const photo = headline?.closest('.product__media-item')?.querySelector('.product__media img');
    if (!headline || !photo) return;

    const measure = () => {
      if (!photo.naturalWidth || !photo.naturalHeight) return;

      try {
        const imageBox = photo.getBoundingClientRect();
        const textBox = headline.getBoundingClientRect();
        if (!imageBox.width || !imageBox.height || !textBox.width || !textBox.height) return;

        const scale = Math.min(imageBox.width / photo.naturalWidth, imageBox.height / photo.naturalHeight);
        const imageLeft = imageBox.left + (imageBox.width - photo.naturalWidth * scale) / 2;
        const imageTop = imageBox.top + (imageBox.height - photo.naturalHeight * scale) / 2;
        const left = Math.max(0, (textBox.left - imageLeft) / scale);
        const top = Math.max(0, (textBox.top - imageTop) / scale);
        const right = Math.min(photo.naturalWidth, (textBox.right - imageLeft) / scale);
        const bottom = Math.min(photo.naturalHeight, (textBox.bottom - imageTop) / scale);
        if (right <= left || bottom <= top) return;

        const canvas = document.createElement('canvas');
        canvas.width = 40;
        canvas.height = 32;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(photo, left, top, right - left, bottom - top, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        let luminance = 0;
        for (let index = 0; index < pixels.length; index += 4) {
          luminance += 0.2126 * pixels[index] + 0.7152 * pixels[index + 1] + 0.0722 * pixels[index + 2];
        }
        headline.dataset.contrast = luminance / (pixels.length / 4) < 210 ? 'light' : 'dark';
      } catch (error) {
        headline.dataset.contrast = 'dark';
      }
    };

    if (photo.complete) measure();
    else photo.addEventListener('load', measure, { once: true });
    if (window.ResizeObserver) {
      gallery.headlineContrastObserver = new ResizeObserver(measure);
      gallery.headlineContrastObserver.observe(headline);
    }
  }

  function updateCounter(gallery, swiper) {
    const current = gallery.querySelector('.slider-counter--current');
    const total = gallery.querySelector('.slider-counter--total');
    if (current) current.textContent = String(swiper.activeIndex + 1);
    if (total) total.textContent = String(swiper.slides.length);
  }

  function setSlideAccessibility(swiper) {
    swiper.slides.forEach((slide, index) => {
      const active = index === swiper.activeIndex;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.querySelectorAll('button, a, iframe, video, model-viewer, [tabindex]').forEach((element) => {
        if (active) {
          if (element.dataset.productSliderTabindex) {
            element.setAttribute('tabindex', element.dataset.productSliderTabindex);
            delete element.dataset.productSliderTabindex;
          } else if (element.getAttribute('tabindex') === '-1') {
            element.removeAttribute('tabindex');
          }
        } else {
          const tabindex = element.getAttribute('tabindex');
          if (tabindex && tabindex !== '-1') element.dataset.productSliderTabindex = tabindex;
          element.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  function activateSlide(gallery, swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    if (!slide?.dataset.mediaId) return;
    gallery.setActiveMedia?.(slide.dataset.mediaId, false, { fromProductSlider: true });
    updateCounter(gallery, swiper);
    setSlideAccessibility(swiper);
    gallery.querySelector('[id^="GalleryViewer"]')?.dispatchEvent(
      new CustomEvent('slideChanged', {
        detail: { currentPage: swiper.activeIndex + 1, currentElement: slide },
      })
    );
  }

  function destroyGallery(gallery) {
    gallery.headlineContrastObserver?.disconnect();
    gallery.headlineContrastObserver = null;
    if (gallery.productSlider && !gallery.productSlider.destroyed) {
      gallery.productSlider.destroy(true, true);
    }
    gallery.setProductSlider?.(null);
    gallery.removeAttribute('data-product-slider-initialized');
  }

  function initGallery(gallery) {
    if (!window.Swiper || !gallery || gallery.productSlider && !gallery.productSlider.destroyed) return;
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const track = gallery.querySelector('[id^="Slider-Gallery"]');
    const slides = track ? Array.from(track.querySelectorAll('[data-media-id]')) : [];
    if (!viewer || !track || !slides.length) return;

    setHeadlineContrast(gallery);

    const activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const swiper = new window.Swiper(viewer, {
      effect: 'slide',
      slidesPerView: 1,
      initialSlide: activeIndex,
      speed: reducedMotion.matches ? 0 : 460,
      threshold: 6,
      resistanceRatio: 0.65,
      grabCursor: true,
      watchSlidesProgress: true,
      keyboard: { enabled: true, onlyInViewport: true },
      navigation: {
        prevEl: gallery.querySelector('.bs-product-slider__button--prev'),
        nextEl: gallery.querySelector('.bs-product-slider__button--next'),
      },
      a11y: {
        enabled: true,
        prevSlideMessage: '前の商品画像へ',
        nextSlideMessage: '次の商品画像へ',
        firstSlideMessage: '最初の商品画像です',
        lastSlideMessage: '最後の商品画像です',
      },
      on: {
        init(instance) { activateSlide(gallery, instance); },
        slideChange(instance) { activateSlide(gallery, instance); },
        slideChangeTransitionEnd(instance) {
          const slide = instance.slides[instance.activeIndex];
          if (slide?.querySelector('video')) gallery.playActiveMedia?.(slide);
        },
        tap(instance, event) {
          const opener = event.target.closest('.swiper-slide-active .product__modal-opener--image');
          opener?.querySelector('.product__media-toggle')?.click();
        },
      },
    });

    gallery.setProductSlider?.(swiper);
    gallery.setAttribute('data-product-slider-initialized', 'true');
  }

  function initAll(root = document) {
    root.querySelectorAll(selector).forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(selector).forEach(destroyGallery);
  });
})();
